import { Stack } from 'component-library';
import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonWidget } from '@/components/widgets/a/Button/Button';
import { useRecommendedIndicatorProduct } from '@/hooks/useRecommendedIndicatorProduct';
import { useToggle } from '@/hooks/useToggle';

import { RecommendedIndicatorProductCard } from './RecommendedIndicatorProductCard';
import { SectionHeader } from './Section';

type SelectedIndicatorsInterface = {
  selectedIndicators: string[];
  setSelectedIndicators: (indicators: string[]) => void;
};

const SelectedIndicatorsContext = createContext<SelectedIndicatorsInterface | null>(null);

export const SelectedIndicatorsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedIndicators, setSelectedIndicators] = useState<string[]>([]);

  const value: SelectedIndicatorsInterface = {
    selectedIndicators,
    setSelectedIndicators,
  };

  return <SelectedIndicatorsContext.Provider value={value}>{children}</SelectedIndicatorsContext.Provider>;
};

export const useSelectedIndicatorsContext = (): SelectedIndicatorsInterface => {
  const selectedIndicatorsContextData = useContext(SelectedIndicatorsContext);

  if (!selectedIndicatorsContextData) {
    throw new Error(
      `The component that calls useSelectedIndicatorsContext() needs to be a deep child of <SelectedIndicatorsProvider>`,
    );
  }

  return selectedIndicatorsContextData;
};

export const OnboardingIndicatorProducts = () => {
  const { data } = useRecommendedIndicatorProduct();
  const indicatorProducts = useMemo(() => data ?? [], [data]);

  const { selectedIndicators, setSelectedIndicators } = useSelectedIndicatorsContext();

  useEffect(() => {
    setSelectedIndicators(indicatorProducts.filter((ip) => ip.is_recommended).map((ip) => ip.id));
  }, [indicatorProducts, setSelectedIndicators]);

  const [isNotRecommendedCollapsed, setIsNotRecommendedCollapsed] = useToggle(true);

  const toggleSelectIndicator = (indicatorId: string) => {
    const newSelectedIndicators = selectedIndicators.includes(indicatorId)
      ? selectedIndicators.filter((id) => id !== indicatorId)
      : [...selectedIndicators, indicatorId];

    setSelectedIndicators(newSelectedIndicators);
  };

  const { t } = useTranslation();

  const notSelectedIndicatorProducts = indicatorProducts.filter(({ id }) => !selectedIndicators.includes(id));

  return (
    <Stack spacing={12}>
      <SectionHeader className='w-1/2' i18nPath='shared.onboarding.indicatorProducts.description' />
      {selectedIndicators.length > 0 && (
        <Stack direction='row' spacing={6} className='flex-wrap w-full max-w-full overflow-hidden'>
          {selectedIndicators.map((indicatorProductId) => {
            const indicatorProduct = indicatorProducts.find((ip) => ip.id === indicatorProductId);

            if (!indicatorProduct) return null;

            return (
              <div key={`${indicatorProduct.name}/${indicatorProduct.widget.type}`}>
                <RecommendedIndicatorProductCard
                  indicatorLibraryCard={indicatorProduct}
                  isSelected={true}
                  toggleSelectIndicator={toggleSelectIndicator}
                />
              </div>
            );
          })}
        </Stack>
      )}
      {notSelectedIndicatorProducts.length > 0 && (
        <Stack spacing={6} className='w-full'>
          <ButtonWidget
            variant='text'
            onClick={setIsNotRecommendedCollapsed}
            className='w-fit hover:bg-transparent px-0 hover:text-primary-hover'
            size='small'
          >
            <span>{t('shared.onboarding.indicatorProducts.exploreAllIndicators')}</span>
            <i className={`ri-arrow-${isNotRecommendedCollapsed ? 'down' : 'up'}-s-line ml-2`} />
          </ButtonWidget>
          {!isNotRecommendedCollapsed && (
            <Stack direction='row' spacing={6} className='flex-wrap w-full max-w-full overflow-hidden'>
              {notSelectedIndicatorProducts.map((indicatorProduct) => {
                const isSelected = selectedIndicators.includes(indicatorProduct.id);

                return (
                  <div key={`${indicatorProduct.name}/${indicatorProduct.widget.type}`}>
                    <RecommendedIndicatorProductCard
                      indicatorLibraryCard={indicatorProduct}
                      isSelected={isSelected}
                      toggleSelectIndicator={toggleSelectIndicator}
                    />
                  </div>
                );
              })}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};
